import { qoursesApi } from '@/api/qourses.tsx'
import { DateSelect } from '@/components/formInputs/DateSelect.tsx'
import Dynamic from '@/components/modals/dynamic.tsx'
import { popModal } from '@/components/modals/index.tsx'
import useGetOrder, { GetOrderQueryKey } from '@/hooks/orders/useGetOrder.tsx'
import { Badge } from '@/shadcn/components/ui/badge.tsx'
import { Button } from '@/shadcn/components/ui/button.tsx'
import { DialogDescription, DialogHeader, DialogTitle } from '@/shadcn/components/ui/dialog.tsx'
import { ToastVariant, minDelay, removePointerEventsFromBody, sendNotification } from '@/utils.tsx'
import { useQueryClient } from '@tanstack/react-query'
import { ClockAlert, Loader2 } from 'lucide-react'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function UpdateOrderPaymentTargetModal({ orderId }: { orderId: string }) {
  const { t: translate } = useTranslation()

  // Prevent radix from creating a sticky pointer events none and breaking the page
  removePointerEventsFromBody()

  const { order, isLoading, isError } = useGetOrder(orderId)

  const queryClient = useQueryClient()

  const [date, setDate] = useState<DateTime | undefined>()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (order && !isLoading) {
      setDate(DateTime.fromISO(order.paymentTarget))
    }
  }, [order, isLoading])

  async function handleUpdateOrderPaymentTarget() {
    try {
      setSubmitting(true)

      await minDelay(
        qoursesApi.payment.paymentControllerChangeOrderPaymentTargetV1(order.id, {
          paymentTarget: date.toISO(),
        }),
        1000,
      )

      await queryClient.invalidateQueries(GetOrderQueryKey(order.id))
      sendNotification(
        translate('modals.updateOrderPaymentTargetModal.notification.title'),
        translate('modals.updateOrderPaymentTargetModal.notification.subtitle', {
          newPaymentTarget: date.toLocaleString(DateTime.DATE_MED),
        }),
        ToastVariant.Success,
      )
    } catch (error) {
      console.error(error)
    } finally {
      setSubmitting(false)
      popModal('UpdateOrderPaymentTargetModal')
    }
  }

  if (isError) {
    return (
      <Dynamic.Content>
        <DialogHeader>
          <DialogTitle>
            {translate('modals.updateOrderPaymentTargetModal.errorState.title')}
          </DialogTitle>
          <DialogDescription>
            {translate('modals.updateOrderPaymentTargetModal.errorState.subtitle')}
          </DialogDescription>
        </DialogHeader>
      </Dynamic.Content>
    )
  }

  return (
    <Dynamic.Content className="p-4 sm:p-8">
      <DialogHeader className="mb-2">
        <DialogTitle className="mt-6 sm:mt-2 flex items-center justify-between">
          {translate('modals.updateOrderPaymentTargetModal.title')}
          {DateTime.fromISO(order.paymentTarget).diffNow().seconds < 0 && (
            <div>
              <Badge variant={'destructive'} className="bezel">
                <ClockAlert className="mr-1 h-4 w-4" />
                {translate('pages.order-detail.basics.paymentTarget.pastDueDate')}
              </Badge>
            </div>
          )}
        </DialogTitle>
        <DialogDescription>
          {translate('modals.updateOrderPaymentTargetModal.subtitle')}
        </DialogDescription>
      </DialogHeader>
      <div className="mt-2 space-y-3 rounded-lg bg-gray-50 p-3 ring-1 ring-gray-100">
        <DateSelect
          focusMonth={date}
          startDate={date}
          setStartDate={setDate}
          title={translate('modals.updateOrderPaymentTargetModal.timeLabel')}
          placeholder={translate('pages.coupon-detail.validity.validFrom.placeholder')}
          label={translate('modals.updateOrderPaymentTargetModal.timeLabelSubtitle')}
          calendarDisabledMatcher={{
            before: new Date(),
          }}
          withTimePicker={true}
          disabled={false}
        />
      </div>
      <div className="mt-4 flex flex-col justify-end">
        <Button
          variant="indigo"
          disabled={!order || !date || DateTime.fromISO(order.paymentTarget).equals(date)}
          onClick={handleUpdateOrderPaymentTarget}
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {translate('common.loading')}
            </>
          ) : (
            translate('modals.updateOrderPaymentTargetModal.button')
          )}
        </Button>
      </div>
    </Dynamic.Content>
  )
}
